import { Box, Button, Divider, FormControl, FormHelperText, Grid, MenuItem, Select, Typography } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useModals } from "../../context/ModalContext";
import { useGlobalSnackbar } from "../../context/SnackbarContext";
import { menuPropsModal } from "../../utils";
import { TextFieldComponent } from "../common/TextFieldComponent";
import { ContactInformationFields } from "../shared/components/forms/contactInformation/ContactInformationFields";
import { colors as color } from "../ui/colors";
import { SASSelectStyles } from "../ui/styles/inputStyles";
import { useVendors } from "./hooks/useVendors";

interface Vendor {
  uid?: string;
  firstName?: string;
  lastName?: string;
  vendorCompanyName?: string;
  alias?: string;
  type?: string;
  status?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  emails?: any[];
  phoneNumbers?: any[];
  [key: string]: any;
}

interface VendorFormProps {
  vendor?: Vendor | null;
  onUpdate?: () => Promise<void> | void;
  mode?: "create" | "update" | "view";
  onCancel?: () => void;
  sx?: any;
  vendorUid?: string;
  vendorForm?: any;
  onSubmitCreate?: (data: any) => void;
  initialPhoneNumber?: string;
  status?: string;
  isProspect?: boolean;
}

export const VendorForm: React.FC<VendorFormProps> = ({
  vendor = null,
  onUpdate,
  mode = "create",
  onCancel,
  sx = {},
  vendorUid,
  vendorForm = null,
  onSubmitCreate = null,
  initialPhoneNumber,
  status: propStatus,
  isProspect = false,
}) => {
  const isView = mode === "view";
  const isCreate = mode === "create";
  const isUpdate = mode === "update";

  const { showSnackbar } = useGlobalSnackbar();
  const { closeByKey } = useModals();

  const { editVendor, getVendorByUid } = useVendors();
  const [fetchedVendor, setFetchedVendor] = useState<any>(null);

  const effectiveVendorUid = vendorUid || (vendor as any)?.vendorUid || (vendor as any)?.uid || fetchedVendor?.uid;

  useEffect(() => {
    if (!effectiveVendorUid) return;

    const hasFullVendorData =
      vendor &&
      (vendor.address ||
        (Array.isArray(vendor.coverageAreas) && vendor.coverageAreas.length > 0) ||
        ((vendor.vendorCompanyName || vendor.firstName) &&
          ((Array.isArray(vendor.emails) && vendor.emails.length > 0) || (Array.isArray(vendor.phoneNumbers) && vendor.phoneNumbers.length > 0))));

    if (hasFullVendorData) return;

    let isMounted = true;
    (async () => {
      try {
        const res = await getVendorByUid(effectiveVendorUid);
        if (isMounted && res?.ok && res?.data) {
          setFetchedVendor(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch vendor by UID", err);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [effectiveVendorUid, vendor, getVendorByUid]);

  const parsedPhone = initialPhoneNumber ? initialPhoneNumber.replace(/\D/g, "") : "";
  const cleanPhone = parsedPhone.startsWith("1") && parsedPhone.length === 11 ? parsedPhone.slice(1) : parsedPhone;
  const prefilledPhones = cleanPhone
    ? [{ countryCode: "US", dialCode: "+1", phone: cleanPhone, phoneNumber: "+1" + cleanPhone }]
    : [{ countryCode: "US", dialCode: "+1", phone: "", phoneNumber: "" }];

  const defaultStatus = propStatus || (isProspect ? "PROSPECT" : "ACTIVE");

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      alias: "",
      address: "",
      city: "",
      state: "",
      country: "",
      zipCode: "",
      emails: [{ email: "", tag: "other", isDefault: false }],
      phones: prefilledPhones,
      type: "INDEPENDENT",
      vendorCompanyName: "",
      status: defaultStatus,
    },
  });

  const [contactState, setContactState] = useState<{ emails: any[]; phones: any[]; isValid: boolean }>({
    emails: [],
    phones: [],
    isValid: true,
  });
  const [showContactErrors, setShowContactErrors] = useState(false);

  const handleContactInfoChange = useCallback((state: any) => {
    setContactState(state);
  }, []);

  const isInitialized = useRef(false);
  const vendorType = watch("type");

  useEffect(() => {
    isInitialized.current = false;
  }, [vendor?.uid, vendor?.updatedAt, fetchedVendor?.uid, fetchedVendor?.updatedAt]);

  useEffect(() => {
    if (isInitialized.current) return;

    const source = isCreate ? (Object.keys(vendorForm || {}).length > 0 ? vendorForm : null) : fetchedVendor || vendor;

    if (source && Object.keys(source).length > 0) {
      let emails = [{ email: "", tag: "other", isDefault: false }];
      if (Array.isArray(source.emails) && source.emails.length > 0) {
        emails = source.emails.map((e: any) =>
          typeof e === "string"
            ? { email: e, tag: "other", isDefault: false }
            : {
                email: e?.value || e?.email || "",
                tag: e?.tag || "other",
                isDefault: !!e?.isDefault,
              },
        );
      } else if (source.email) {
        emails = [{ email: source.email, tag: "other", isDefault: true }];
      }

      let phones = prefilledPhones;
      if (Array.isArray(source.phoneNumbers) && source.phoneNumbers.length > 0) {
        phones = source.phoneNumbers.map((p: any) => ({
          countryCode: p.countryCode || "US",
          dialCode: p.dialCode || "+1",
          phone: p.phone || "",
          phoneNumber: (p.dialCode || "+1") + (p.phone || ""),
          tag: p.tag || undefined,
          isDefault: p.isDefault || undefined,
        }));
      } else if (Array.isArray(source.phones) && source.phones.length > 0) {
        phones = source.phones.map((p: any) => ({
          countryCode: p.countryCode || "US",
          dialCode: p.dialCode || "+1",
          phone: p.phone || "",
          phoneNumber: (p.dialCode || "+1") + (p.phone || ""),
          tag: p.tag || undefined,
          isDefault: p.isDefault || undefined,
        }));
      } else if (source.phone) {
        phones = [
          {
            countryCode: source.countryCode || "US",
            dialCode: source.dialCode || "+1",
            phone: source.phone,
            phoneNumber: (source.dialCode || "+1") + (source.phone || ""),
          },
        ];
      }

      const coverageArea0 = Array.isArray(source.coverageAreas) && source.coverageAreas[0] ? source.coverageAreas[0] : null;
      const addressVal = source.address || coverageArea0?.address || "";
      const cityVal = source.city || coverageArea0?.city || "";
      const stateVal = source.state || coverageArea0?.state || "";
      const countryVal = source.country || coverageArea0?.country || "";
      const zipCodeVal = source.zipCode || coverageArea0?.zipCode || "";

      const formData = {
        firstName: source.firstName || "",
        lastName: source.lastName || "",
        alias: source.alias || "",
        address: addressVal,
        city: cityVal,
        state: stateVal,
        country: countryVal,
        zipCode: zipCodeVal,
        emails,
        phones,
        type: source.type || "INDEPENDENT",
        vendorCompanyName: source.vendorCompanyName || "",
        status: source.status || defaultStatus,
      };
      reset(formData);
      isInitialized.current = true;
    } else if (isCreate && !vendorForm) {
      isInitialized.current = true;
    }
  }, [isCreate, vendor, fetchedVendor, reset, vendorForm]);

  const onCBSubmit = async (data: any) => {
    if (isView) return;

    if (!contactState.isValid) {
      setShowContactErrors(true);
      return;
    }

    const mappedEmails = Array.isArray(contactState.emails)
      ? contactState.emails
          .map((e: any) => {
            if (typeof e === "string") {
              return {
                email: e,
                tag: "other",
                isDefault: false,
              };
            }
            return {
              email: e?.email || e?.value || "",
              tag: e?.tag || "other",
              isDefault: !!e?.isDefault,
            };
          })
          .filter((e: any) => e.email)
      : [];

    const hasEmail = mappedEmails.length > 0;
    const hasPhone = contactState.phones.some((p: any) => p?.phone);

    if (!hasEmail && !hasPhone) {
      setShowContactErrors(true);
      showSnackbar("You have not provided an email or phone number. Please provide at least one contact method to continue.", "warning");
      return;
    }

    const submitData = {
      ...data,
      emails: mappedEmails,
      phones: contactState.phones,
    };

    if (isCreate) {
      onSubmitCreate?.(submitData);
      return;
    }

    const targetUid = effectiveVendorUid;

    if (isUpdate && targetUid) {
      try {
        const { email, phone, phones, emails, ...cleanedData } = submitData;

        const mappedPhoneNumbers = Array.isArray(phones)
          ? phones
              .map((p: any) => {
                const cleanPhone = typeof p.phone === "string" ? p.phone.replace(/\D/g, "") : "";
                const dialCode = p.dialCode || "+1";
                return {
                  countryCode: p.countryCode || "US",
                  dialCode: dialCode,
                  phone: cleanPhone,
                  phoneNumber: dialCode + cleanPhone,
                  tag: p.tag || undefined,
                  isDefault: p.isDefault || undefined,
                };
              })
              .filter((p: any) => p.phone)
          : [];

        const payload = {
          ...cleanedData,
          vendorCompanyName: vendorType === "CORPORATE" ? cleanedData.vendorCompanyName || null : null,
          alias: vendorType === "CORPORATE" ? cleanedData.alias || null : null,
          emails: submitData.emails,
          phoneNumbers: mappedPhoneNumbers,
        };

        const res = await editVendor(targetUid, payload);

        if (res?.ok) {
          showSnackbar("Vendor updated successfully", "success");
          await onUpdate?.();
        } else {
          showSnackbar("Update failed. Please try again and check the form.", "error");
        }
      } catch (err) {
        showSnackbar("Update failed. Please try again and check the form.", "error");
      }
    }
  };

  const vendorTypes = [
    { name: "Independent", id: "INDEPENDENT" },
    { name: "Corporate", id: "CORPORATE" },
  ];

  const vendorStatuses = [
    { name: "Active", id: "ACTIVE" },
    { name: "Prospect", id: "PROSPECT" },
    { name: "Pending", id: "PENDING" },
    { name: "Suspended", id: "SUSPENDED" },
    { name: "Inactive", id: "INACTIVE" },
  ];

  const SectionTitle = ({ children, icon }: { children: React.ReactNode; icon?: boolean }) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        {icon && (
          <Box
            sx={{
              width: 4,
              height: 24,
              bgcolor: color.orange?.base || "#FF6B35",
              borderRadius: 2,
              mr: 2,
            }}
          />
        )}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: color.gray?.[800] || "var(--fx-surface-text, #0F172A)",
            letterSpacing: 0.5,
          }}
        >
          {children}
        </Typography>
      </Box>
      <Divider
        sx={{
          borderColor: color.gray?.[200] || "var(--fx-surface-border, #E2E8F0)",
          opacity: 0.8,
        }}
      />
    </Box>
  );

  const REGEX_LETTERS_ONLY = /^[A-Za-zÀ-ÿ\s]+$/;

  return (
    <Box
      id="vendor-form-general-info"
      component="form"
      onSubmit={handleSubmit(onCBSubmit)}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "flex-start",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        gap: 3,
        py: 2,
        px: { xs: 1.5, sm: 3 },
        mx: 0,
        ...sx,
      }}
    >
      {/* Vendor information */}
      <Box sx={{ width: "100%", boxSizing: "border-box" }}>
        <SectionTitle icon>Vendor Information</SectionTitle>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                color: color.gray?.[600] || "var(--fx-surface-textsecondary, #64748B)",
                fontWeight: 600,
                letterSpacing: 0.5,
                mb: 0.5,
              }}
            >
              Vendor Type
            </Typography>

            <Controller
              name="type"
              control={control}
              rules={{ required: "Vendor type is required" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth error={!!error} sx={{ ...SASSelectStyles, mt: 0.5, mb: 0.5 }}>
                  <Select
                    {...field}
                    MenuProps={menuPropsModal}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    disabled={isView}
                  >
                    {vendorTypes.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                color: color.gray?.[600] || "var(--fx-surface-textsecondary, #64748B)",
                fontWeight: 600,
                letterSpacing: 0.5,
                mb: 0.5,
              }}
            >
              Vendor Status
            </Typography>

            <Controller
              name="status"
              control={control}
              rules={{ required: "Vendor status is required" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth error={!!error} sx={{ ...SASSelectStyles, mt: 0.5, mb: 0.5 }}>
                  <Select
                    {...field}
                    MenuProps={menuPropsModal}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    disabled={isView}
                  >
                    {vendorStatuses.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>

          {vendorType === "CORPORATE" && (
            <Grid item xs={12} md={12}>
              <TextFieldComponent
                name="vendorCompanyName"
                errors={errors}
                register={register("vendorCompanyName", {
                  required: {
                    value: vendorType === "CORPORATE",
                    message: "Company name is required for Corporate vendors",
                  },
                  minLength: { value: 2, message: "Minimum of two characters" },
                })}
                label="Company Name"
                inputLabel="Company Name"
                disabled={isView}
              />
            </Grid>
          )}

          <Grid item xs={12} md={4}>
            <TextFieldComponent
              name="alias"
              errors={errors}
              register={register("alias", {
                minLength: { value: 2, message: "Minimum of two characters" },
              })}
              label="Alias"
              inputLabel="Alias"
              disabled={isView}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextFieldComponent
              name="firstName"
              errors={errors}
              register={register("firstName", {
                required: {
                  value: true,
                  message: "First name is required",
                },
                minLength: { value: 2, message: "Minimum of two characters" },
                pattern: {
                  value: REGEX_LETTERS_ONLY,
                  message: "Only letters are allowed",
                },
              })}
              label="First Name"
              inputLabel="First Name"
              disabled={isView}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextFieldComponent
              name="lastName"
              errors={errors}
              register={register("lastName", {
                minLength: { value: 2, message: "Minimum of two characters" },
                pattern: {
                  value: REGEX_LETTERS_ONLY,
                  message: "Only letters are allowed",
                },
              })}
              label="Last Name"
              inputLabel="Last Name"
              disabled={isView}
            />
          </Grid>

          <Grid item xs={12}>
            <TextFieldComponent
              name="address"
              errors={errors}
              register={register("address")}
              label="Address"
              inputLabel="Address"
              disabled={isView}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Contact information */}
      <Box sx={{ width: "100%", boxSizing: "border-box" }}>
        <ContactInformationFields
          key={
            isInitialized.current
              ? `${effectiveVendorUid || "initialized"}-${getValues("phones")?.[0]?.phone || "empty"}-${getValues("emails")?.[0]?.email || "empty"}`
              : "loading"
          }
          initialEmails={getValues("emails")}
          initialPhones={getValues("phones")}
          onChange={handleContactInfoChange}
          disabled={isView}
          requireEither={true}
          showErrors={showContactErrors}
        />
      </Box>

      {(vendor || effectiveVendorUid || isUpdate) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            mt: 4,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Button
            onClick={() => {
              if (effectiveVendorUid) {
                closeByKey(`update-vendor-${effectiveVendorUid}`);
              }
              closeByKey("Vendor-Details-Modal");
              closeByKey("Virtual-Receptionist-Contact-Details-Modal");
              onCancel?.();
            }}
            sx={{
              px: 3,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              color: color.gray?.[600] || "var(--fx-surface-textsecondary, #64748B)",
              border: `1px solid ${color.gray?.[300] || "var(--fx-surface-borderstrong, #CBD5E1)"}`,
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              "&:hover": {
                backgroundColor: color.gray?.[100] || "var(--fx-surface-sunken, #F8FAFC)",
                borderColor: color.gray?.[400] || "var(--fx-surface-borderstrong, #CBD5E1)",
              },
            }}
          >
            {isView ? "Close" : "Cancel"}
          </Button>

          {!isView && (
            <Button
              type="submit"
              sx={{
                px: 3,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                color: "#fff",
                backgroundColor: color.orange?.base || "#FF6B35",
                "&:hover": {
                  backgroundColor: color.orange?.[600] || "#E25822",
                },
              }}
            >
              Update
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
